import React, { useEffect, useState } from 'react'
import { Button, Card, Modal, notification, Switch } from 'antd'
import idx from 'idx'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'
import { fetchAlertById, fetchAlertsList, fetchAllAlerts, removeAlertById, resetUpdateAlertStatus, updateAlert } from '~actions'
import { AlertInfo, ResultModal } from '~components'
import { getCurrentUser } from '~context'
import { PageLayout, Spinner } from '~stories'

import styles from './EditGlobalAlert.module.css'

const NOTIFICATION_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action.'
}

const EditGlobalAlert = ({
  alerts,
  allVenues,
  deleteMessage,
  deleteStatus,
  deletedAlert,
  fetchAlertsList,
  fetchAllAlerts,
  fetchGlogalAlert,
  removeAlert,
  resetUpdateAlertStatus,
  route,
  alert,
  updateMessage,
  updateAlert,
  updateStatus
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState()
  const [alertId, setAlertId] = useState('')
  const [alertToEdit, setAlertToEdit] = useState({})
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()

  // Fetch existing alerts and venues for overlap checking
  useEffect(() => {
    fetchAlertsList({})
    fetchAllAlerts()
  }, [fetchAlertsList, fetchAllAlerts])

  useEffect(() => {
    const alertId = idx(route, _ => _.match.params.id)
    if (alertId) {
      setIsDataLoaded(false)
      fetchGlogalAlert(alertId)
      setAlertId(alertId)
    }
  }, [route, fetchGlogalAlert])

  useEffect(() => {
    if (alert) {
      setAlertToEdit(alert)
      setIsDataLoaded(true)
    }
  }, [alert])

  useEffect(() => {
    if (updateStatus && updateMessage) {
      setIsDataLoaded(true)
    } else if (!updateStatus && updateMessage) {
      setIsDataLoaded(true)
      notification.error({ message: updateMessage })
      resetUpdateAlertStatus()
    }
  }, [updateStatus, updateMessage, resetUpdateAlertStatus])

  useEffect(() => {
    if (deleteStatus && deleteMessage) {
      setIsDataLoaded(true)
    } else if (!deleteStatus && deleteMessage) {
      setIsDataLoaded(true)
      notification.error({ message: deleteMessage })
    }
  }, [deleteStatus, deleteMessage, deletedAlert, route])

  const handleSubmit = () => {
    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }

    // Validation: Check for mandatory fields
    const errors = []

    // Validate venue
    if (!alertToEdit?.venue_id) {
      errors.push('Venue is required')
    }

    // Validate message (strip HTML tags to check for actual content)
    const messageContent = alertToEdit?.message || ''
    const textContent = messageContent.replace(/<[^>]*>/g, '').trim()
    if (!textContent) {
      errors.push('Message is required')
    }

    // Validate color
    if (!alertToEdit?.color) {
      errors.push('Color is required')
    }

    // If there are validation errors, show them and return
    if (errors.length > 0) {
      notification.error({
        message: 'Validation Error',
        description: errors.join(', ')
      })
      return
    }

    setIsDataLoaded(false)
    const updatedAlert = {
      ...alertToEdit
    }
    updateAlert(alertId, updatedAlert, accessToken)
  }

  const keepEditingAlert = (
    <Route key="editAlerts" render={({ history }) => (
      <Button
        onClick={() => {
          resetUpdateAlertStatus()
          history.push(`/global-alerts/${alertId}/edit`)
        }}
        type="primary"
      >
        Keep Editing
      </Button>
    )} />
  )

  const goToListAlertsButton = (
    <Route key="listAlerts" render={({ history }) => (
      <Button
        onClick={() => {
          resetUpdateAlertStatus()
          history.push('/global-alerts')
        }}
        type="primary"
      >
        Go to Alerts List
      </Button>
    )} />
  )

  const handleMessageChange = htmlDescription => {
    setAlertToEdit({
      ...alertToEdit,
      message: htmlDescription
    })
  }

  const handleModalClose = () => {
    resetUpdateAlertStatus()
  }

  const handleAlertStatus = status => {
    setAlertToEdit({
      ...alertToEdit,
      active: status
    })
  }
  const handleUrlUpdate = ({ target }) => {
    setAlertToEdit({
      ...alertToEdit,
      url: target.value
    })
  }

  // Derive color from type if color is not set (backward compatibility)
  const getColorValue = (color) => {
    if (color === '#9ACBFF') return '#9ACBFF'
    if (color === '#DA0D50') return '#DA0D50'
    if (color === '#7827AA') return '#7827AA'
    return undefined
  }

  const handleTypeChange = (color) => {
    // Derive type from color value
    setAlertToEdit({
      ...alertToEdit,
      color: getColorValue(color) || ''
    })
  }

  const handleStartDateChange = (date) => {
    if (!date) {
      setAlertToEdit({
        ...alertToEdit,
        schedule: {
          ...alertToEdit?.schedule,
          start_date: null
        }
      })
      return
    }

    // Get venue timezone
    const selectedVenue = allVenues.find(v => v.value === alertToEdit?.venue_id)
    const venueTimezone = selectedVenue?.timezone || 'America/New_York'

    // Convert date to Unix timestamp based on venue timezone
    const dateInVenueTz = moment.tz(
      date.format('YYYY-MM-DD HH:mm:ss'),
      venueTimezone
    )

    setAlertToEdit({
      ...alertToEdit,
      schedule: {
        ...alertToEdit?.schedule,
        start_date: dateInVenueTz.unix()
      }
    })
  }

  const handleEndDateChange = (date) => {
    if (!date) {
      setAlertToEdit({
        ...alertToEdit,
        schedule: {
          ...alertToEdit?.schedule,
          end_date: null
        }
      })
      return
    }

    // Get venue timezone
    const selectedVenue = allVenues.find(v => v.value === alertToEdit?.venue_id)
    const venueTimezone = selectedVenue?.timezone || 'America/New_York'

    // Convert date to Unix timestamp based on venue timezone
    const dateInVenueTz = moment.tz(
      date.format('YYYY-MM-DD HH:mm:ss'),
      venueTimezone
    )

    // Validate that end date is greater than start date
    if (alertToEdit?.schedule?.start_date) {
      const startDate = typeof alertToEdit.schedule.start_date === 'number'
        ? moment.unix(alertToEdit.schedule.start_date)
        : moment(alertToEdit.schedule.start_date)
      const endDate = dateInVenueTz

      if (!endDate.isAfter(startDate)) {
        notification.error({
          message: 'Validation Error',
          description: 'End date must be greater than start date'
        })
        return // Don't update state
      }
    }

    setAlertToEdit({
      ...alertToEdit,
      schedule: {
        ...alertToEdit?.schedule,
        end_date: dateInVenueTz.unix()
      }
    })
  }

  const handleCookieExpirationChange = (date) => {
    setAlertToEdit({
      ...alertToEdit,
      cookie_expiration_date: date ? date.unix() : null
    })
  }

  const handleIconChange = (imgObj) => {
    const clonedAlert = { ...alertToEdit }
    clonedAlert.icon = { ...clonedAlert.icon, ...imgObj }
    setAlertToEdit(clonedAlert)
  }

  const handleImageUrlUpdate = (imageUrl) => {
    const clonedAlert = { ...alertToEdit }
    clonedAlert.icon = { ...clonedAlert.icon, url: imageUrl }
    setAlertToEdit(clonedAlert)
  }

  const handleShowAlertOnPromoChange = (e) => {
    setAlertToEdit({
      ...alertToEdit,
      show_alert_on_promo: e.target.checked || false
    })
  }

  const handleDeleteAlert = () => {
    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }

    Modal.confirm({
      title: 'Delete Alert',
      content: 'This alert will be removed permanently. Are you sure you want to delete this alert?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        setIsDataLoaded(false)
        removeAlert(alertId, accessToken)
      }
    })
  }

  const updateAlertButton = <Button className={styles.updateActionButton} onClick={handleSubmit} type='primary'>
    Update
  </Button>

  const deleteAlertButton = (
    <Button danger onClick={handleDeleteAlert} type='default'>
      Delete
    </Button>
  )

  const toggleSwitch = <Switch checked={alertToEdit?.active} checkedChildren="Show" defaultChecked onChange={handleAlertStatus} unCheckedChildren="Hide" />

  return (
    <PageLayout extra={[toggleSwitch, deleteAlertButton, updateAlertButton]} title='Update Global Alert'>
      <div className={deleteStatus ? styles.blurred : ''}>
        <Card>
          <AlertInfo
            alert={alertToEdit}
            allVenues={allVenues || []}
            existingAlerts={alerts || []}
            handleCookieExpirationChange={handleCookieExpirationChange}
            handleEndDateChange={handleEndDateChange}
            handleIconChange={handleIconChange}
            handleImageUrlUpdate={handleImageUrlUpdate}
            handleMessageChange={handleMessageChange}
            handleShowAlertOnPromoChange={handleShowAlertOnPromoChange}
            handleStartDateChange={handleStartDateChange}
            handleTypeChange={handleTypeChange}
            handleUrlUpdate={handleUrlUpdate}
            handleVenueChange={(venueId) => {
              setAlertToEdit({
                ...alertToEdit,
                venue_id: venueId
              })
            }}
          />
        </Card>
        <Spinner isLoading={!isDataLoaded}/>
      </div>
      {
        (updateStatus === true && updateMessage === 'Alert updated successfully') &&
        <ResultModal
          actions={[
            goToListAlertsButton,
            keepEditingAlert
          ]}
          handleCancel={handleModalClose}
          status="success"
          title={updateMessage}
        />
      }
      {
        (deleteStatus && deleteMessage) &&
        <ResultModal
          actions={[
            goToListAlertsButton
          ]}
          handleCancel={handleModalClose}
          status="success"
          title={deleteMessage}
        />
      }
    </PageLayout>
  )
}

EditGlobalAlert.propTypes = {
  alert: PropTypes.object,
  alerts: PropTypes.array,
  allVenues: PropTypes.array,
  deleteMessage: PropTypes.string,
  deleteStatus: PropTypes.bool,
  deletedAlert: PropTypes.object,
  fetchAlertsList: PropTypes.func,
  fetchAllAlerts: PropTypes.func,
  fetchGlogalAlert: PropTypes.func,
  removeAlert: PropTypes.func,
  resetUpdateAlertStatus: PropTypes.func,
  route: PropTypes.object.isRequired,
  updateAlert: PropTypes.func,
  updateMessage: PropTypes.string,
  updateStatus: PropTypes.bool
}

const mapStateToProps = ({ alerts }) => ({
  alerts: alerts.alerts || [],
  allVenues: alerts.allVenues || [],
  alert: alerts.alert,
  deleteMessage: alerts.deleteMessage,
  deleteStatus: alerts.deleteStatus,
  updateStatus: alerts.success,
  updateMessage: alerts.message,
  deletedAlert: alerts.deletedAlert
})

const mapDispatchToProps = {
  fetchAlertsList,
  fetchAllAlerts,
  fetchGlogalAlert: fetchAlertById,
  removeAlert: removeAlertById,
  resetUpdateAlertStatus,
  updateAlert
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditGlobalAlert)
